import {useAtom} from "jotai";
import {activeMapFeatureAtom} from "../../store/activeMapFeatureStore";
import {Box, ButtonGroup, IconButton, ImageList, ImageListItem, Stack, Typography} from "@mui/material";
import dayjs from "dayjs";
import {useEffect, useState} from "react";
import {
    downloadScoutingNotePDF,
    downloadScoutingNoteZIP,
    getScoutingNoteDetails,
} from "../../services/scoutingNotesService";
import {ScoutingNote} from "../../types/scoutingNote";
import ImageDialog from "./imageDialog";
import {useTranslation} from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import {LoadingButton} from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import {mapRefAtom} from "../../store/mapSelectedLayersStore";
import {MapRef} from "react-map-gl";
import {useUserPreferences} from "../../hooks/userPreferencesHooks";
import {useCompany} from "../../hooks/companiesHooks";

export const ScoutingNoteComponent: React.FC = () => {
    const {t} = useTranslation();
    const [scoutingNote] = useAtom(activeMapFeatureAtom);
    const activeCompany = useCompany();
    const [scoutingNoteDetails, setScoutingNoteDetails] = useState<ScoutingNote>();
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [imageIndex, setImageIndex] = useState(0);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [zipLoading, setZipLoading] = useState(false);
    const [, setActiveMapFeature] = useAtom(activeMapFeatureAtom);
    const [mapRef] = useAtom(mapRefAtom);
    const {dateFormat} = useUserPreferences();

    const readonlyStyle = {
        backgroundColor: "#e9ebf1",
        padding: "5px 10px",
        marginBottom: "10px",
        borderRadius: "4px",
    };

    const main = {
        p: 3,
        bgcolor: "background.paper",
        width: "100%",
        minWidth: "320px",
        maxWidth: "320px",
        backgroundColor: "white",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    };

    const title = {
        textTransform: "uppercase",
        color: "#737990",
        fontSize: 20,
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid",
        borderColor: "rgba(0, 0, 0, 0.12)",
    };

    useEffect(() => {
        const fetchPhotos: () => unknown = async () => {
            try {
                if (scoutingNote && activeCompany?.data) {
                    const scoutingNoteDetails: ScoutingNote = await getScoutingNoteDetails(
                        activeCompany?.data?.id,
                        scoutingNote.fieldId,
                        scoutingNote.id,
                    );

                    setScoutingNoteDetails(scoutingNoteDetails);
                }
            } catch (error) {
                console.error("Error fetching photos batch:", error);
                throw error;
            }
        };

        fetchPhotos();
    }, [scoutingNote, activeCompany?.data]);

    function showPictureModal(index: number) {
        setImageIndex(index);
        setShowImageDialog(true);
    }

    const getPdf = () => {
        if (!scoutingNote || !activeCompany) {
            return;
        }

        function takeScreenshot(map: MapRef): Promise<string> {
            return new Promise((resolve, reject) => {
                if (!map) {
                    reject(new Error("Map instance is required"));
                    return;
                }

                map.once("render", () => {
                    const canvas = map.getCanvas();
                    if (!canvas) {
                        reject(new Error("Canvas element not found"));
                        return;
                    }
                    resolve(canvas.toDataURL("image/png"));
                });

                map.setBearing(map.getBearing());
            });
        }

        const resizeBase64Image = (base64Image: string, width: number, height: number): Promise<string> => {
            return new Promise((resolve) => {
                const img = new Image();
                img.src = base64Image;

                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                        canvas.width = width;

                        ctx.drawImage(img, 0, 0, width, height);

                        const resizedBase64Image = canvas.toDataURL("image/png");
                        resolve(resizedBase64Image);
                    }
                };
            });
        };

        if (mapRef) {
            takeScreenshot(mapRef)
                .then(async (scoutingMapData) => {
                    if (!activeCompany?.data?.id) {
                        return;
                    }

                    setPdfLoading(true);

                    const resizedMapData = await resizeBase64Image(scoutingMapData, 250, 250);

                    await downloadScoutingNotePDF(
                        activeCompany.data.id,
                        scoutingNote.fieldId,
                        scoutingNote.id,
                        resizedMapData,
                        {
                            field_information: t("scoutingnotes.PDF.field_information"),
                            company_name: t("scoutingnotes.PDF.company_name"),
                            farm_name: t("scoutingnotes.PDF.farm_name"),
                            field_name: t("scoutingnotes.PDF.field_name"),
                            crop_type: t("scoutingnotes.PDF.crop_type"),
                            planting_date: t("scoutingnotes.PDF.planting_date"),
                            scouting_note: t("scoutingnotes.PDF.scouting_note"),
                            date: t("scoutingnotes.PDF.date"),
                            coordinates: t("scoutingnotes.PDF.coordinates"),
                            scouting_map: t("scoutingnotes.PDF.scouting_map"),
                            notes: t("scoutingnotes.PDF.notes"),
                            observation_pictures: t("scoutingnotes.PDF.observation_pictures"),
                            main_category_title: t("scoutingnotes.PDF.main_category"),
                            detailed_category_title: t("scoutingnotes.PDF.detailed_category"),
                            main_category: t(`scoutingnotes.main_categories.${scoutingNote.category.mainCategory}`),
                            detailed_category: t(
                                `scoutingnotes.detailed_categories.${scoutingNote.category.detailedCategory}`,
                            ),
                        },
                    );
                    setPdfLoading(false);
                })
                .catch((error) => {
                    console.error("Error taking screenshot:", error);
                });
        }
    };

    const getZip = async () => {
        if (!scoutingNote || !activeCompany) {
            return;
        }

        try {
            setZipLoading(true);
            if (activeCompany?.data?.id) {
                await downloadScoutingNoteZIP(activeCompany?.data?.id, scoutingNote.fieldId, scoutingNote.id);
            }
            setZipLoading(false);
        } catch (error) {
            setZipLoading(false);
            console.error("Error downloading ZIP", error);
        }
    };

    return (
        <Box sx={main}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <Typography sx={title}>
                    {t("scoutingnotes.title")}
                    <IconButton
                        sx={{
                            color: "#737990",
                            display: "flex",
                        }}
                        onClick={() => setActiveMapFeature(null)}
                    >
                        <CloseIcon />
                    </IconButton>
                </Typography>
            </Box>

            <Stack spacing={2} sx={{mt: 4}}>
                <Box>
                    <Typography textTransform="uppercase" color="text.secondary">
                        {t("scoutingnotes.date")}
                    </Typography>
                    <Typography className="input-readonly" sx={readonlyStyle}>
                        {scoutingNote?.date ? dayjs(scoutingNote.date).format(dateFormat) : ""}
                    </Typography>

                    <Typography textTransform="uppercase" color="text.secondary">
                        {t("scoutingnotes.location")}
                    </Typography>
                    <Typography className="input-readonly" sx={readonlyStyle}>
                        {scoutingNote?.location?.coordinates
                            ? `${scoutingNote.location.coordinates[0].toFixed(5)}, ${scoutingNote.location.coordinates[1].toFixed(5)}`
                            : "N/A"}
                    </Typography>

                    <Typography textTransform="uppercase" color="text.secondary">
                        {t("scoutingnotes.main_category")}
                    </Typography>
                    <Typography className="input-readonly" sx={readonlyStyle}>
                        {t(`scoutingnotes.main_categories.${scoutingNote?.category?.mainCategory}`)}
                    </Typography>

                    <Typography textTransform="uppercase" color="text.secondary">
                        {t("scoutingnotes.detailed_category")}
                    </Typography>
                    <Typography className="input-readonly" sx={readonlyStyle}>
                        {t(`scoutingnotes.detailed_categories.${scoutingNote?.category?.detailedCategory}`)}
                    </Typography>

                    <Typography textTransform="uppercase" color="text.secondary">
                        {t("scoutingnotes.note")}
                    </Typography>
                    <Typography
                        className="input-readonly"
                        sx={{...readonlyStyle, ...{maxHeight: "200px", overflowY: "auto", overflowX: "hidden"}}}
                    >
                        {scoutingNote?.note}
                    </Typography>
                </Box>
            </Stack>

            {scoutingNoteDetails?.urls?.length && (
                <ButtonGroup
                    variant="outlined"
                    aria-label="Loading button group"
                    fullWidth
                    sx={{margin: "16px 0px 8px 0px"}}
                >
                    <LoadingButton
                        startIcon={<DownloadIcon />}
                        onClick={() => {
                            void getPdf();
                        }}
                        loading={pdfLoading}
                    >
                        PDF
                    </LoadingButton>
                    <LoadingButton
                        onClick={() => {
                            void getZip();
                        }}
                        startIcon={<DownloadIcon />}
                        loading={zipLoading}
                    >
                        ZIP
                    </LoadingButton>
                </ButtonGroup>
            )}

            <ImageList sx={{width: 320}} cols={2}>
                {scoutingNoteDetails?.urls && scoutingNoteDetails.urls.length > 0 ? (
                    scoutingNoteDetails.urls.map((data, index) => (
                        <ImageListItem key={data?.url}>
                            <img
                                srcSet={data?.url}
                                src={data?.url}
                                loading="lazy"
                                style={{cursor: "pointer", borderRadius: "4px"}}
                                onClick={() => showPictureModal(index)}
                            />
                        </ImageListItem>
                    ))
                ) : (
                    <div>{t("scoutingnotes.errors.no_images_available")}</div>
                )}
            </ImageList>

            <ImageDialog
                open={showImageDialog}
                images={scoutingNoteDetails?.mediumUrls?.map((val) => val.url) ?? []}
                onClose={() => setShowImageDialog(false)}
                activeImageIndex={imageIndex}
            ></ImageDialog>
        </Box>
    );
};
